import ControlList from "slotworks/smart-ui/control-list";
import { Control } from "slotworks/smart-ui/controls/control";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import Contract from "slotworks/utils/contracts/contract";

export interface SmartUIGroup {
    show(): Contract<any>;
    hide(): void;
    addControl(control: Control, name: string): void;
    getControl<T extends Control>(controlList: ControlList<Control>): T;
    updateOptions(options: ControlOptions): void;
    addChangeListener(listener: (vo: any) => void): void;
    isEnabled(): boolean;
    addEnableCondition(flags: SmartUIFlag[]): void;
    addDisableCondition(flags: SmartUIFlag[]): void;
    smartUIFlagsUpdated(bitmask: number): void;
    setEnabled(enabled: boolean): void;
}

export default class HeadlessSmartUIGroup implements SmartUIGroup {

    public name: string;

    protected enabled: boolean;
    protected enableConditions: number[] = [];
    protected disableConditions: number[] = [];
    protected flagsOnShow: SmartUIFlag[] = [];
    protected controls: Control[];
    protected visible: boolean = true;

    constructor(name: string) {
        this.name = name;

        this.controls = [];
    }

    public show(): Contract<any> {
        this.controls.forEach((control) => {
            control.setVisible(true);
        });

        this.visible = true;

        return Contract.empty();
    }

    public hide(): void {
        this.controls.forEach((control) => control.setVisible(false));

        this.visible = false;
    }

    public addControl(control: Control, name: string = "headless"): void {
        this.controls.push(control);
        control.setup(name, null, null, null, null);
    }

    public getControl<T extends Control>(controlList: ControlList<Control>): T {
        for (const control of this.controls) {
            if (control.getName() === controlList.getName()) {
                return control as T;
            }
        }
    }

    public updateOptions(options: ControlOptions): void {
        for (const control of this.controls) {
            control.updateOptions(options);
        }
    }

    public addChangeListener(listener: (vo: any) => void): void {
        for (const control of this.controls) {
            control.getOnChange().add(listener);
        }
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public isVisible(): boolean {
        return this.visible;
    }

    /**
     * If ALL specified flags match, enable the group (disable takes priority if they match too)
     */
    public addEnableCondition(flags: SmartUIFlag[]): void {
        this.enableConditions.push(this.generateBitmask(flags));

        SmartUIManager.refresh();
    }

    /**
     * If ALL specified flags match, disable the group (takes priority over enable if they match too)
     */
    public addDisableCondition(flags: SmartUIFlag[]): void {
        this.disableConditions.push(this.generateBitmask(flags));

        SmartUIManager.refresh();
    }

    /**
     * When this group is shown, automatically toggle these flags on (and back off again when it's hidden)
     */
    public setFlagsOnShow(flags: SmartUIFlag[]) {
        this.flagsOnShow = flags;
    }

    public smartUIFlagsUpdated(bitmask: number): void {
        let enabled = true;

        if (this.hasEnableConditions()) {
            enabled = false;
            if (this.checkEnableConditions(bitmask)) {
                enabled = true;
            }
        }

        if (this.hasDisableConditions()) {
            if (this.checkDisableConditions(bitmask)) {
                enabled = false;
            }
        }

        this.setEnabled(enabled);
    }

    public setEnabled(enabled: boolean): void {
        if (this.enabled !== enabled) {
            this.enabled = enabled;

            for (const control of this.controls) {
                control.setEnabled(enabled);
            }
        }
    }

    private hasEnableConditions(): boolean {
        return this.enableConditions.length > 0;
    }

    private hasDisableConditions(): boolean {
        return this.disableConditions.length > 0;
    }

    private checkEnableConditions(bitmask: number) {
        return this.enableConditions.some((condition) => (condition & bitmask) === condition);
    }

    private checkDisableConditions(bitmask: number) {
        return this.disableConditions.some((condition) => (condition & bitmask) === condition);
    }

    private generateBitmask(flags: SmartUIFlag[]) {
        let bitmask = 0;
        for (const flag of flags) {
            bitmask |= flag;
        }

        return bitmask;
    }
}
